import { useRef, useState } from 'react';
import { toggleObjectInclusion } from './utils/objectSelection';

export enum SelectionDeltaState {
  INIT,
  ACTIVE
}

export interface ObjectsSelectionDelta {
  allSelect?: boolean;
  inclusions?: string[];
  exclusions?: string[];
}

export interface UseObjectsSelectionResponse {
  isSelectionDeltaReset: () => boolean;
  selectionDelta: ObjectsSelectionDelta;
  toggleItemInclusion: (objectID: string, included: boolean) => void;
  toggleListInclusion: (included: boolean) => void;
  setCountsIfReset: (counts: { total: number; selected: number; disabledCount: number }) => void;
  resetSelectionDelta: VoidFunction;
  selected?: number;
  disabledCount?: number;
  total?: number;
  isListIncluded?: boolean;
  isListIndeterminate: boolean;
  isSelectionEmpty: boolean;
  isSelectionDeltaEmpty: boolean;
}

export function useObjectsSelection(): UseObjectsSelectionResponse {
  const selectionDeltaStateRef = useRef(SelectionDeltaState.INIT);

  const [state, setState] = useState<{
    selectionDelta: ObjectsSelectionDelta;
    selected?: number;
    total?: number;
    disabledCount?: number;
    isListIncluded?: boolean;
  }>({
    selectionDelta: {}
  });

  const resetSelectionDelta = () => {
    selectionDeltaStateRef.current = SelectionDeltaState.INIT;
  };

  const setCountsIfReset = ({
    total,
    selected,
    disabledCount
  }: {
    total: number;
    selected: number;
    disabledCount: number;
  }) => {
    if (selectionDeltaStateRef.current === SelectionDeltaState.INIT) {
      selectionDeltaStateRef.current = SelectionDeltaState.ACTIVE;

      setState({
        ...state,
        selectionDelta: {},
        isListIncluded: selected === total,
        selected,
        disabledCount,
        total
      });
    }
  };

  const { selectionDelta, total, selected, isListIncluded } = state;

  const toggleItemInclusion = (objectID: string, included: boolean) => {
    const newSelected = included ? (state.selected || 0) + 1 : (state.selected || 0) - 1;
    const newSelectionDelta = { ...state.selectionDelta };

    const { exclusions, inclusions } = toggleObjectInclusion(
      newSelectionDelta.inclusions,
      newSelectionDelta.exclusions,
      objectID,
      included
    );

    newSelectionDelta.exclusions = exclusions;
    newSelectionDelta.inclusions = inclusions;

    let isListIncludedNewState = state.isListIncluded;

    if (newSelected === 0) {
      isListIncludedNewState = false;
    } else if (newSelected === state.total) {
      isListIncludedNewState = true;
    }

    setState({
      ...state,
      selectionDelta: newSelectionDelta,
      selected: newSelected,
      isListIncluded: isListIncludedNewState
    });
  };

  const toggleListInclusion = (included: boolean) => {
    setState({
      ...state,
      selectionDelta: {
        allSelect: included
      },
      selected: included ? (state.total || 0) - (state.disabledCount || 0) : 0,
      isListIncluded: included && state.disabledCount === 0
    });
  };

  const isSelectionDeltaReset = () => {
    return selectionDeltaStateRef.current === SelectionDeltaState.INIT;
  };

  const isSelectionDeltaEmpty =
    typeof state.selectionDelta.allSelect !== 'boolean'
      ? !state.selectionDelta.inclusions && !state.selectionDelta.exclusions
      : false;

  return {
    selectionDelta,
    isSelectionDeltaReset,
    resetSelectionDelta,
    setCountsIfReset,
    toggleItemInclusion,
    toggleListInclusion,
    selected,
    total,
    isListIncluded,
    isListIndeterminate: !!(selected && total && selected < total),
    isSelectionEmpty: selected === 0,
    isSelectionDeltaEmpty
  };
}
